import { gql } from './client';
import { onEvent } from '../ws';

export interface PresignedPost {
  url: string;
  fields: Record<string, string>;
  s3Key: string;
}

export async function emit(
  eventType: string,
  entityId: string,
  payload: Record<string, unknown>,
  connectionId?: string,
): Promise<string> {
  const res = await gql<{ emitEvent: { eventId: string } }>(
    `mutation EmitPlanningEvent($entityId: ID!, $payload: JSON!, $connectionId: String) {
      emitEvent(eventType: "${eventType}", entityId: $entityId, payload: $payload, connectionId: $connectionId) { eventId }
    }`,
    { entityId, payload, connectionId: connectionId ?? null },
  );
  return res.emitEvent.eventId;
}

export function withExpected(
  patch: Record<string, unknown>,
  expectedValues?: Record<string, unknown>,
): Record<string, unknown> {
  return expectedValues && Object.keys(expectedValues).length ? { ...patch, expectedValues } : patch;
}

export const LOST_CONNECTION = 'The connection to the server dropped before the result arrived.';
/** The wait for an event's workflows ran out: they may well still finish, so this is not a failure. */
export const STILL_PROCESSING = 'Still processing — the request is taking longer than expected. Refresh to check.';

export function waitForEvent(eventId: string, timeoutMs = 15_000): Promise<void> {
  return new Promise((resolve, reject) => {
    onEvent(eventId, (n) => {
      if (n.status === 'completed') resolve();
      else if (n.status === 'failed') reject(new Error(n.error ?? 'Operation failed'));
      else reject(new Error(n.status === 'disconnected' ? LOST_CONNECTION : STILL_PROCESSING));
    }, timeoutMs);
  });
}

// Like waitForEvent, but hands back the finished workflow's step context. An immediate-mode event carries it as `results`;
// a batch-mode one nests it per workflow under `workflowResults`.
export function waitForEventResults(eventId: string, timeoutMs = 15_000): Promise<Record<string, unknown>> {
  return new Promise((resolve, reject) => {
    onEvent(eventId, (n) => {
      if (n.status === 'completed') {
        const merged: Record<string, unknown> = { ...(n.results ?? {}) };
        for (const wf of Object.values(n.workflowResults ?? {})) Object.assign(merged, wf.results ?? {});
        resolve(merged);
      } else if (n.status === 'failed') reject(new Error(n.error ?? 'Operation failed'));
      else reject(new Error(n.status === 'disconnected' ? LOST_CONNECTION : STILL_PROCESSING));
    }, timeoutMs);
  });
}

export async function postToS3(url: string, fields: Record<string, string>, file: Blob): Promise<void> {
  const form = new FormData();
  for (const [k, v] of Object.entries(fields)) form.append(k, v);
  form.append('file', file);
  const res = await fetch(url, { method: 'POST', body: form });
  if (!res.ok) throw new Error(`Upload failed (${res.status})`);
}

export function firstOrThrow<T>(rows: T[] | null | undefined, message: string): T {
  const row = rows?.[0];
  if (!row) throw new Error(message);
  return row;
}
